// Bidirectional translation between a PM-friendly visual model and the
// json-logic wire format the backend `FieldConditionCodec` accepts. PMs never
// see raw json-logic: the editor renders [[VisualRule]] / [[ListSizeConfig]] /
// [[CriticalityConfig]], and we compile to / decompile from json-logic here.
//
// Anything a human authored that does NOT decompile cleanly (a shape outside
// this operator set) returns `null` from the decompile functions — the editor
// then falls back to a read-only raw-JSON view so an existing rule is never
// silently rewritten or corrupted.
//
// Operator allow-list mirrors `FieldConditionCodec` (backend) and
// `data-collection.helpers.ts` (FE evaluator). Keep the three in lock-step.

import type { DataCriticality, FieldCondition } from './product-requirements-config.dto';

export type ComparisonOperator =
    | 'equals'
    | 'not_equals'
    | 'is_empty'
    | 'is_not_empty'
    | 'gt'
    | 'gte'
    | 'lt'
    | 'lte'
    | 'includes'
    | 'count_gt'
    | 'count_gte'
    | 'count_eq'
    | 'count_lt'
    | 'count_lte';

/** Operators that take no right-hand value. */
export const UNARY_OPERATORS: ReadonlySet<ComparisonOperator> = new Set(['is_empty', 'is_not_empty']);

/** Operators whose right-hand value must be a number. */
const NUMERIC_OPERATORS: ReadonlySet<ComparisonOperator> = new Set([
    'gt',
    'gte',
    'lt',
    'lte',
    'count_gt',
    'count_gte',
    'count_eq',
    'count_lt',
    'count_lte',
]);

export interface VisualCondition {
    /** Referenced field code — the `{"var": code}` operand. */
    field: string;
    operator: ComparisonOperator;
    /** Right-hand value; ignored for unary operators. */
    value: string | number;
}

export type CombineMode = 'all' | 'any';

export interface VisualRule {
    mode: CombineMode;
    conditions: VisualCondition[];
}

export const EMPTY_RULE: VisualRule = { mode: 'all', conditions: [] };

// ── boolean rules (visibleWhen / requiredWhen) ───────────────────────────────

function varNode(field: string): FieldCondition {
    return { var: field } as unknown as FieldCondition;
}

function numericValue(value: string | number): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
}

function compileCondition(c: VisualCondition): FieldCondition {
    const v = varNode(c.field);
    const rhs = NUMERIC_OPERATORS.has(c.operator) ? numericValue(c.value) : c.value;
    switch (c.operator) {
        case 'equals':
            return { '==': [v, rhs] } as unknown as FieldCondition;
        case 'not_equals':
            return { '!=': [v, rhs] } as unknown as FieldCondition;
        case 'is_empty':
            return { not_exists: [v] } as unknown as FieldCondition;
        case 'is_not_empty':
            return { exists: [v] } as unknown as FieldCondition;
        case 'gt':
            return { '>': [v, rhs] } as unknown as FieldCondition;
        case 'gte':
            return { '>=': [v, rhs] } as unknown as FieldCondition;
        case 'lt':
            return { '<': [v, rhs] } as unknown as FieldCondition;
        case 'lte':
            return { '<=': [v, rhs] } as unknown as FieldCondition;
        case 'includes':
            // json-logic `in` is [needle, haystack]: "field contains value".
            return { in: [rhs, v] } as unknown as FieldCondition;
        case 'count_gt':
            return { count_gt: [v, rhs] } as unknown as FieldCondition;
        case 'count_gte':
            return { count_gte: [v, rhs] } as unknown as FieldCondition;
        case 'count_eq':
            return { count_eq: [v, rhs] } as unknown as FieldCondition;
        case 'count_lt':
            return { count_lt: [v, rhs] } as unknown as FieldCondition;
        case 'count_lte':
            return { count_lte: [v, rhs] } as unknown as FieldCondition;
        default:
            throw new Error(`unhandled operator ${c.operator}`);
    }
}

/** Compile a visual rule to json-logic, or `null` when there are no conditions. */
export function compileRule(rule: VisualRule): FieldCondition | null {
    if (rule.conditions.length === 0) return null;
    if (rule.conditions.length === 1) return compileCondition(rule.conditions[0]);
    const children = rule.conditions.map(compileCondition);
    const key = rule.mode === 'all' ? 'and' : 'or';
    return { [key]: children } as unknown as FieldCondition;
}

function asRecord(node: unknown): Record<string, unknown> | null {
    return node && typeof node === 'object' && !Array.isArray(node) ? (node as Record<string, unknown>) : null;
}

/** Read the field code out of a `{"var": code}` / `{"var": [code, …]}` node. */
function readVar(node: unknown): string | null {
    const rec = asRecord(node);
    if (!rec || Object.keys(rec).length !== 1 || !('var' in rec)) return null;
    const operand = rec.var;
    if (typeof operand === 'string') return operand;
    if (Array.isArray(operand) && typeof operand[0] === 'string') return operand[0];
    return null;
}

const LEAF_BY_OP: Record<string, ComparisonOperator> = {
    '==': 'equals',
    '!=': 'not_equals',
    '>': 'gt',
    '>=': 'gte',
    '<': 'lt',
    '<=': 'lte',
    count_gt: 'count_gt',
    count_gte: 'count_gte',
    count_eq: 'count_eq',
    count_lt: 'count_lt',
    count_lte: 'count_lte',
};

function decompileCondition(node: unknown): VisualCondition | null {
    const rec = asRecord(node);
    if (!rec || Object.keys(rec).length !== 1) return null;
    const op = Object.keys(rec)[0];
    const operand = rec[op];

    if (op === 'exists' || op === 'not_exists') {
        const arr = Array.isArray(operand) ? operand : [operand];
        const field = readVar(arr[0]);
        if (field == null) return null;
        return { field, operator: op === 'exists' ? 'is_not_empty' : 'is_empty', value: '' };
    }

    if (op === 'in') {
        if (!Array.isArray(operand) || operand.length !== 2) return null;
        const field = readVar(operand[1]);
        const value = operand[0];
        if (field == null || (typeof value !== 'string' && typeof value !== 'number')) return null;
        return { field, operator: 'includes', value };
    }

    const operator = LEAF_BY_OP[op];
    if (!operator) return null;
    if (!Array.isArray(operand) || operand.length !== 2) return null;
    const field = readVar(operand[0]);
    const value = operand[1];
    if (field == null || (typeof value !== 'string' && typeof value !== 'number')) return null;
    return { field, operator, value };
}

/**
 * Decompile json-logic into a visual rule. Returns `null` when the shape isn't
 * representable in the builder (caller shows the raw/advanced view).
 */
export function decompileRule(node: FieldCondition): VisualRule | null {
    const rec = asRecord(node);
    if (rec && Object.keys(rec).length === 1 && (rec.and || rec.or)) {
        const mode: CombineMode = rec.and ? 'all' : 'any';
        const children = (rec.and ?? rec.or) as unknown;
        if (!Array.isArray(children) || children.length === 0) return null;
        const conditions: VisualCondition[] = [];
        for (const child of children) {
            const c = decompileCondition(child);
            if (!c) return null;
            conditions.push(c);
        }
        return { mode, conditions };
    }
    const single = decompileCondition(node);
    return single ? { mode: 'all', conditions: [single] } : null;
}

// ── list size (LIST fields) ──────────────────────────────────────────────────

export type ListSizeMode = 'free' | 'fixed' | 'fromField';

export interface ListSizeConfig {
    mode: ListSizeMode;
    /** mode === 'fixed' */
    fixed?: number;
    /** mode === 'fromField' — sibling field code */
    field?: string;
}

export const FREE_LIST_SIZE: ListSizeConfig = { mode: 'free' };

export function compileListSize(config: ListSizeConfig): FieldCondition | null {
    if (config.mode === 'free') return null;
    if (config.mode === 'fixed') return (config.fixed ?? 0) as unknown as FieldCondition;
    return varNode(config.field ?? '');
}

export function decompileListSize(node: FieldCondition | null): ListSizeConfig | null {
    if (node == null) return FREE_LIST_SIZE;
    if (typeof node === 'number') return { mode: 'fixed', fixed: node };
    const field = readVar(node);
    if (field != null) return { mode: 'fromField', field };
    return null;
}

// ── criticality override (criticalityWhen) ───────────────────────────────────

const CRITICALITY_NAMES: DataCriticality[] = ['mandatory', 'important', 'optional', 'irrelevant'];

export interface CriticalityRule {
    when: VisualRule;
    /** Resolved criticality when `when` matches. */
    criticality: DataCriticality;
}

export interface CriticalityConfig {
    rules: CriticalityRule[];
    /** Fallback when no rule matches. */
    fallback: DataCriticality;
}

/**
 * Compile an ordered (when → criticality) chain into a json-logic `if`. The
 * backend resolves the result against `MANDATORY|IMPORTANT|OPTIONAL|IRRELEVANT`
 * (uppercase names), so we emit uppercase literals. Returns `null` when there
 * are no rules (no override; the static `dataCriticality` stands).
 */
export function compileCriticality(config: CriticalityConfig): FieldCondition | null {
    const usable = config.rules.filter((r) => r.when.conditions.length > 0);
    if (usable.length === 0) return null;
    const branches: unknown[] = [];
    for (const rule of usable) {
        branches.push(compileRule(rule.when));
        branches.push(rule.criticality.toUpperCase());
    }
    branches.push(config.fallback.toUpperCase());
    return { if: branches } as unknown as FieldCondition;
}

function asCriticality(value: unknown): DataCriticality | null {
    if (typeof value !== 'string') return null;
    const lower = value.toLowerCase() as DataCriticality;
    return CRITICALITY_NAMES.includes(lower) ? lower : null;
}

// ── LIST-parent aggregation (criticalityWhen on the LIST parent's assignment) ─

export type ListEntryRequirement = 'at_least_one' | 'every';

/**
 * The "how strict is this list" template: parent-level aggregation per
 * condition-dsl.md — `{"if": [{"some"|"all": [{"var": ownCode}]}, SAT, MISS]}`.
 * The one-operand `some`/`all` binds the parent's own code to its entries'
 * per-row validity booleans (backend extension), so "at least one entry
 * complete" / "every entry complete" become a two-select choice instead of
 * hand-written json-logic.
 */
export interface ListAggregationConfig {
    requirement: ListEntryRequirement;
    /** Criticality once the aggregation is satisfied (typically 'optional'). */
    satisfied: DataCriticality;
    /** Criticality while it is not — what the list counts as when incomplete. */
    missing: DataCriticality;
}

export function compileListAggregation(config: ListAggregationConfig, ownCode: string): FieldCondition {
    const op = config.requirement === 'at_least_one' ? 'some' : 'all';
    return {
        if: [{ [op]: [varNode(ownCode)] }, config.satisfied.toUpperCase(), config.missing.toUpperCase()],
    } as unknown as FieldCondition;
}

/**
 * Recognizes exactly the shape [[compileListAggregation]] emits: a single-key
 * `if` with three branches whose condition is a ONE-operand `some`/`all` over
 * the field's own code, and two criticality literals. Anything else — the
 * two-operand predicate form, extra branches, a different collection — returns
 * `null` and lands in the generic/advanced views instead of being rewritten.
 */
export function decompileListAggregation(node: FieldCondition | null, ownCode: string): ListAggregationConfig | null {
    if (node == null) return null;
    const rec = asRecord(node);
    if (!rec || Object.keys(rec).length !== 1 || !Array.isArray(rec.if)) return null;
    const branches = rec.if as unknown[];
    if (branches.length !== 3) return null;
    const cond = asRecord(branches[0]);
    if (!cond || Object.keys(cond).length !== 1) return null;
    const op = Object.keys(cond)[0];
    if (op !== 'some' && op !== 'all') return null;
    const operand = cond[op];
    if (!Array.isArray(operand) || operand.length !== 1) return null;
    if (readVar(operand[0]) !== ownCode) return null;
    const satisfied = asCriticality(branches[1]);
    const missing = asCriticality(branches[2]);
    if (!satisfied || !missing) return null;
    return { requirement: op === 'some' ? 'at_least_one' : 'every', satisfied, missing };
}

export function decompileCriticality(
    node: FieldCondition | null,
    staticCriticality: DataCriticality,
): CriticalityConfig | null {
    if (node == null) return { rules: [], fallback: staticCriticality };
    const rec = asRecord(node);
    if (!rec || Object.keys(rec).length !== 1 || !Array.isArray(rec.if)) return null;
    const branches = rec.if as unknown[];
    // `if` must be [cond, then, cond, then, …, else] — an odd length.
    if (branches.length < 3 || branches.length % 2 === 0) return null;
    const rules: CriticalityRule[] = [];
    for (let i = 0; i + 1 < branches.length - 1; i += 2) {
        const when = decompileRule(branches[i] as FieldCondition);
        const criticality = asCriticality(branches[i + 1]);
        if (!when || !criticality) return null;
        rules.push({ when, criticality });
    }
    const fallback = asCriticality(branches[branches.length - 1]);
    if (!fallback) return null;
    return { rules, fallback };
}
